import { Button, Stack, Typography, fontStacks } from '@munifocus/ui';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { FileDetail } from '@munifocus/contracts-gov';
import { AppShell } from './AppShell';
import { FileEmergencyBanner } from './FileEmergencyBanner';
import {
  ESCALATION_STEP_REF,
  escalationStepLabelKey,
  type EscalationStep,
} from '../lib/escalation-display';
import { localizeStatuteRef } from '../lib/file-display';

/**
 * Shared chrome for the Type 3 sub-flow step screens (Steps 5–9). Mirrors
 * StageLayout but keys off an escalation step rather than a file stage — the
 * file-level stage stays at `escalation` for the whole sub-flow.
 */
export function EscalationStepLayout({
  file,
  step,
  children,
  onSave,
  saving,
  disabled,
  saveLabel,
}: {
  file: FileDetail;
  step: EscalationStep;
  children: ReactNode;
  onSave: () => void;
  saving: boolean;
  disabled?: boolean;
  saveLabel?: string;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const legislativeRef = ESCALATION_STEP_REF[step];

  return (
    <AppShell
      back={{ label: file.reference, onClick: () => navigate(`/files/${file.id}`) }}
      title={t(escalationStepLabelKey(step))}
      bottomBar={
        <Stack direction="row" spacing={1}>
          <Button fullWidth variant="outlined" onClick={() => navigate(`/files/${file.id}`)}>
            {t('common.actions.back')}
          </Button>
          <Button fullWidth variant="contained" disabled={saving || disabled} onClick={onSave}>
            {saving ? t('common.actions.saving') : (saveLabel ?? t('common.actions.save'))}
          </Button>
        </Stack>
      }
    >
      <FileEmergencyBanner emergency={file.emergency} />
      <Typography variant="body2" color="text.secondary" sx={{ fontFamily: fontStacks.mono }}>
        {file.reference}
      </Typography>
      <Stack direction="row" sx={{ alignItems: 'baseline', justifyContent: 'space-between' }}>
        <Typography variant="h5">{t(escalationStepLabelKey(step))}</Typography>
        {legislativeRef && (
          <Typography variant="caption" color="text.secondary">
            {localizeStatuteRef(legislativeRef)}
          </Typography>
        )}
      </Stack>

      <Stack spacing={2} sx={{ mt: 2 }}>
        {children}
      </Stack>
    </AppShell>
  );
}
